// @flow
import { getSignupOptions } from "api";
import { postSignupUser } from "api/auth";
import { AuthContext } from "auth/authContext";
import { signIn } from "aws-amplify/auth";
import Loading from "components/Loading";
import { useLoadProducts } from "queries/payments/useLoadProducts";
import * as React from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet";
import Ui from "ui";
import ErrorLabel from "ui/atoms/ErrorLabel";
import PhoneField from "ui/molecules/PhoneFieldAlt";
import SelectField from "ui/molecules/SelectField";
import TextField from "ui/molecules/TextField";
import { objectHasPropWithValue } from "utils/helpers";
import {
  error,
  initial,
  isError,
  isLoading,
  isSuccess,
  loading,
  success,
} from "utils/remoteData";
import {
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  newPasswordSchema,
  organizationSchema,
  requiredPhoneSchema,
  schemasValidate,
} from "utils/schemas";
import { ZentakeContext } from "utils/ZentakeContext";
import * as yup from "yup";

import ActionLink from "./components/ActionLink";
import AuthCard from "./components/AuthCard";

type Props = {
  history: {
    replace: (path: string) => void,
  },
  location: {
    search: string,
  },
};

const SignupSchema = {
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  password: newPasswordSchema,
  organization: organizationSchema,
  plan: yup.string().nullable(),
  product: yup.string().nullable(),
  phone: requiredPhoneSchema,
  specialty: yup.string().required("Please select a specialty"),
};

function Signup({ history, location }: Props): React.Node {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { plans, updatePlans, isFetchingPlans, setGlobalLoading } =
    React.useContext(ZentakeContext);
  const { data: products, isLoading: isProductsLoading } = useLoadProducts();

  const { isAuthenticated, handleRedirectCallback } =
    React.useContext(AuthContext);

  const hasPlans = (plans?.length ?? 0) > 0;
  const hasProducts = (products?.length ?? 0) > 0;

  const [formValues, setFormValues] = React.useState<{
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    organization: string,
    plan: ?string,
    product: ?string,
    phone: string,
    specialty: string,
  }>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    organization: "",
    plan: undefined,
    product: undefined,
    phone: "",
    specialty: "",
  });

  const [formError, setFormError] = React.useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    organization: false,
    plan: false,
    product: false,
    phone: false,
  });

  const [formErrorLabel, setFormErrorLabel] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    organization: "",
    plan: "",
    product: "",
  });

  const [hasFormError, setHasFormError] = React.useState(false);
  const [singupRequest, setSignupRequest] = React.useState(initial());
  const [specialties, setSpecialties] = React.useState([]);

  React.useEffect(() => {
    async function loadSpecialties() {
      try {
        const response = await getSignupOptions();
        const options = response.actions.POST.specialty.choices.map(
          (choice) => ({
            label: choice.display_name,
            value: choice.value,
          }),
        );
        setSpecialties(options);
      } catch (err) {
        setSpecialties([]);
      }
    }

    function init() {
      return Promise.all([updatePlans(), loadSpecialties()]);
    }
    init();
  }, [updatePlans]);

  React.useEffect(() => {
    setHasFormError(objectHasPropWithValue(formError, true));
  }, [formError]);

  React.useEffect(() => {
    if (isAuthenticated) {
      history.replace("/");
    }
  }, [history, isAuthenticated, singupRequest]);

  const onChange = React.useCallback(
    (name: string) => (value) => {
      setFormValues((current) => ({ ...current, [name]: value }));

      formError[name] &&
        schemasValidate(
          [[name, value, SignupSchema[name]]],
          setFormError,
          setFormErrorLabel,
        );
    },
    [formError],
  );

  const onSubmit = React.useCallback(async () => {
    const allSchemas = Object.keys(SignupSchema).map((objKey) => [
      objKey,
      formValues[objKey],
      SignupSchema[objKey],
    ]);
    const isValid = await schemasValidate(
      allSchemas,
      setFormError,
      setFormErrorLabel,
    );
    if (!formValues.plan && !formValues.product) {
      setFormError((prev) => ({ ...prev, plan: true, product: true }));
      setFormErrorLabel((prev) => ({
        ...prev,
        plan: "Please select a plan or product",
        product: "Please select a product or plan",
      }));
      return;
    }
    if (!isValid) {
      return;
    }
    setSignupRequest(loading());
    try {
      const token = await executeRecaptcha("signup");
      // $FlowFixMe
      const data = await postSignupUser({ recaptcha: token, ...formValues });
      const response = await signIn({
        username: formValues.email,
        password: formValues.password,
      });
      setGlobalLoading(true);
      setSignupRequest(success(formValues));
      if (response.isSignedIn && response.nextStep.signInStep === "DONE") {
        await handleRedirectCallback();
      }
      const redirectPath = data.no_cc ? "/verify-email" : "/signup/setup-card";
      history.replace(redirectPath);
    } catch (err) {
      setSignupRequest(error(err.response?.data?.detail || err.message));
    }
  }, [
    executeRecaptcha,
    formValues,
    handleRedirectCallback,
    history,
    setGlobalLoading,
  ]);

  const { search } = location;
  const queryString = search.substring(1, search.length);
  const urlSearchParams = new URLSearchParams(queryString);
  let selectedPlanId = null;
  let selectedProductId = null;

  for (const param of urlSearchParams) {
    if (param[0] === "plan") {
      selectedPlanId = param[1];
      break;
    }
  }

  for (const param of urlSearchParams) {
    if (param[0] === "product") {
      selectedProductId = param[1];
      break;
    }
  }

  const selectedPlan = plans?.find((plan) => plan.id === selectedPlanId);
  const selectedProduct = products?.find(
    (product) => product.id === selectedProductId,
  );

  React.useEffect(() => {
    if (selectedPlan) {
      onChange("plan")(selectedPlan.id);
    }
  }, [selectedPlan, onChange]);

  React.useEffect(() => {
    if (selectedProduct) {
      onChange("product")(selectedProduct.id);
    }
  }, [selectedProduct, onChange]);

  const zentakePricingUrl = "https://www.zentake.com/pricing";

  if (
    !selectedPlanId &&
    !selectedProductId &&
    !isSuccess(singupRequest) &&
    !isAuthenticated
  ) {
    window.location.href = zentakePricingUrl;
    return <Loading />;
  }

  if (isFetchingPlans || isProductsLoading) {
    return <Loading />;
  }

  if (
    hasPlans &&
    hasProducts &&
    !selectedPlan &&
    !selectedProduct &&
    !isSuccess(singupRequest) &&
    !isAuthenticated
  ) {
    window.location.href = zentakePricingUrl;
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>Signup/Zentake</title>
      </Helmet>

      {isSuccess(singupRequest) ? (
        <Loading />
      ) : (
        <AuthCard wide>
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-x-6">
            <div className="mt-3">
              <TextField
                errorLabel={formErrorLabel.firstName}
                hasError={formError.firstName}
                label="First Name"
                name="firstName"
                onChange={onChange("firstName")}
                placeholder="Type your first name"
                type="text"
                value={formValues.firstName}
              />
            </div>
            <div className="mt-3">
              <TextField
                errorLabel={formErrorLabel.lastName}
                hasError={formError.lastName}
                label="Last Name"
                name="lastName"
                onChange={onChange("lastName")}
                placeholder="Type your last name"
                type="text"
                value={formValues.lastName}
              />
            </div>
          </div>
          <div className="mt-3">
            <TextField
              errorLabel={formErrorLabel.organization}
              hasError={formError.organization}
              label="Organization"
              name="organization"
              onChange={onChange("organization")}
              placeholder="Type the organization name"
              type="text"
              value={formValues.organization}
            />
          </div>
          <div className="mt-3">
            <SelectField
              semibold
              showPlaceholder
              errorLabel={formErrorLabel.specialty}
              hasError={formError.specialty}
              label="Specialty"
              name="specialty"
              onChange={(e) => onChange("specialty")(e.target.value)}
              options={specialties}
              placeholder="Select a specialty"
              value={formValues.specialty}
            />
          </div>
          <div className="mt-3">
            <TextField
              errorLabel={formErrorLabel.email}
              hasError={formError.email}
              label="Email"
              name="email"
              onChange={onChange("email")}
              placeholder="Type your email"
              type="text"
              value={formValues.email}
            />
          </div>
          <div className="mt-3">
            <PhoneField
              errorLabel={formErrorLabel.phone}
              hasError={formError.phone}
              label="Phone"
              maxLength={14}
              name="phone"
              onChange={onChange("phone")}
              placeholder="Phone number"
              value={formValues.phone}
            />
          </div>
          <div className="mt-6">
            <Ui.PasswordInput
              errorLabel={formErrorLabel.password}
              hasError={formError.password}
              label="Password"
              name="password"
              onChange={onChange("password")}
              placeholder="Type your password"
              type="password"
              value={formValues.password}
            />
          </div>

          <div className="grid mt-6">
            <Ui.PrimaryButton
              disabled={hasFormError}
              loading={isLoading(singupRequest)}
              onClick={onSubmit}
            >
              Signup
            </Ui.PrimaryButton>
            <p className="mb-2 mt-1 text-xs text-gray-600 justify-self-center">
              By clicking this button, you agree to our{" "}
              <a
                className="underline"
                href="https://www.zentake.com/terms-of-service"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                className="underline"
                href="https://www.zentake.com/privacy-policy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>
              .
            </p>
            <div className="mx-px mt-2 flex flex-1 justify-between flex-shrink-0">
              <ActionLink to="/login">Already have an account?</ActionLink>
            </div>
          </div>
          {isError(singupRequest) && (
            <div className="flex flex-1 justify-center mt-3">
              <ErrorLabel>{singupRequest.error}</ErrorLabel>
            </div>
          )}
        </AuthCard>
      )}
    </>
  );
}

function withReCaptcha(props: Props): React.Node {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
      <Signup {...props} />
    </GoogleReCaptchaProvider>
  );
}

export default withReCaptcha;
